import { useEffect } from 'react';

interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  tags: string[];
  category: string;
  url?: string;
  client?: string;
  year?: string;
  fullDescription?: string;
}

interface ProjectModalProps {
  project: Project;
  onClose: () => void;
}

export default function ProjectModal({ project, onClose }: ProjectModalProps) {
  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Handle ESC key
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-black border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors duration-300 cursor-pointer"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* Image */}
        <div className="relative w-full aspect-video">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="p-8 lg:p-12">
          {/* Title */}
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            {project.title}
          </h2>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-6 mb-8 pb-8 border-b border-white/10">
            {project.client && (
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Kunde</p>
                <p className="text-white text-base">{project.client}</p>
              </div>
            )}
            {project.year && (
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Jahr</p>
                <p className="text-white text-base">{project.year}</p>
              </div>
            )}
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Kategorie</p>
              <p className="text-white text-base capitalize">{project.category}</p>
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-white mb-4">Über das Projekt</h3>
            <p className="text-white/60 text-base lg:text-lg leading-relaxed">
              {project.fullDescription || project.description}
            </p>
          </div>

          {/* Tags */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-white mb-4">Leistungen</h3>
            <div className="flex flex-wrap gap-3">
              {project.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="text-sm text-white/60 px-4 py-2 border border-white/20 uppercase tracking-wider"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-[#d1bc8a] text-black px-8 py-4 text-sm uppercase tracking-wider font-medium hover:bg-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <span>Website besuchen</span>
              <i className="ri-external-link-line text-lg"></i>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}